import axios from 'axios'
import { Platform } from 'quasar'
import { Preferences } from '@capacitor/preferences'

import store from '../store'

const ENDPOINT = '/etraining_login/set_user_device_token/'

// Instancia isolada de proposito. A instancia global do axios tem um interceptor
// de resposta que, num 401 sem refresh_token, limpa a sessao e faz
// router.replace({ name: 'login' }) — e este POST sai do boot, sem interacao do
// usuario, entao usar o axios global significaria poder deslogar alguem durante a
// abertura do app. O axios.create() copia os defaults mas nunca os handlers de
// interceptor, o que torna isso impossivel aqui.
//
// O contrapartida e nao ter o retry com refresh automatico. E aceitavel: a chave
// de deduplicacao so e mantida em caso de sucesso, entao uma falha se resolve
// sozinha no proximo retorno ao primeiro plano.
let client = null

function getClient() {
	if (!client) {
		client = axios.create({
			baseURL: axios.defaults.baseURL || process.env.API_URL,
			// O global usa timeout 0 (infinito); aqui um POST pendurado para sempre
			// seguraria a chave de deduplicacao e bloquearia as tentativas seguintes.
			timeout: 15000,
		})
	}

	return client
}

// No iOS o WKWebView pode descartar o localStorage onde o vuex-persist guarda o
// estado, e por isso o interceptor em src/boot/axios.js le o access_token do
// Preferences. Aqui a precedencia e invertida de proposito: o SAVE_ACCESS_TOKEN
// escreve state.access_token de forma sincrona e so depois faz await
// Preferences.set, entao no login o store ja tem o token novo enquanto o
// Preferences ainda tem o antigo. O store nunca fica mais velho que o
// Preferences, o que faz "store primeiro, Preferences como fallback" cobrir os
// dois casos: o login e o localStorage descartado.
async function getAccessToken() {
	const access_token = store.getters['common/access_token']

	if (access_token) return access_token

	if (Platform.is.capacitor && Platform.is.ios) {
		try {
			const pref = await Preferences.get({ key: 'access_token' })

			if (pref && pref.value) return pref.value
		} catch (e) {
			console.log('[push] erro ao ler o access_token do Preferences:', e)
		}
	}

	return ''
}

// Ultimo par token+usuario enviado com sucesso nesta execucao. O id do usuario
// entra na chave porque o token do dispositivo e estavel por meses: sem ele, um
// segundo usuario logando no mesmo aparelho seria ignorado pela deduplicacao e o
// device continuaria vinculado ao usuario anterior no backend.
//
// Como a variavel zera a cada cold start, o app reenvia o token uma vez por
// abertura — que e justamente o que conserta a sessao persistida (o vuex-persist
// mantem o usuario logado por meses e o setUserData do login nunca mais roda).
let last_sent_key = null

export default async function sendDeviceToken(token, { force = false } = {}) {
	if (!token) return false

	// Sem sessao o backend responde ValidationError ("Nao sei quem e voce").
	// O LoginForm cobre esse caso logo apos autenticar.
	const access_token = await getAccessToken()

	if (!access_token) {
		console.log('[push] token recebido sem sessao, envio adiado')
		return false
	}

	const key = `${token}|${store.getters['common/id'] || ''}`

	if (!force && last_sent_key === key) return false

	// Marcado antes do POST para que dois eventos em sequencia (tokenReceived do
	// boot + appStateChange) nao disparem duas requisicoes concorrentes. Em caso
	// de erro a marca e desfeita abaixo, entao o valor final continua sendo o
	// ultimo par efetivamente aceito pelo backend.
	const previous_key = last_sent_key
	last_sent_key = key

	try {
		const fd = new FormData()
		fd.append('token', token)
		fd.append('device_type', Platform.is.android ? 'android' : 'ios')

		// Sem content-type: com body FormData o axios remove o header para o
		// browser inserir o boundary. Sem interceptor nesta instancia, o
		// authorization tem que ser montado aqui.
		await getClient().post(ENDPOINT, fd, {
			headers: { authorization: `Bearer ${access_token}` },
		})

		console.log('[push] token registrado no backend:', token)
		return true
	} catch (e) {
		last_sent_key = previous_key
		console.log('[push] falha ao registrar token no backend:', e?.response?.status, e)
		return false
	}
}
